// Users section of the admin console: the user list and the user page.
// Who may change what is decided by the server (src/controller/user_admin.py)
// and re-checked on every request; the choices come from GET /api/users/meta.
//
// The user page is ONE page of cards — profile, access, modules, data access,
// language model, Assistant Bro, Reasoning Engine — next to a summary column with the section
// links. A card is only shown when it matters for this user (Assistant Bro
// settings only with Assistant Bro or the chat widget, the Reasoning Engine
// only with its module). The username is the identifier everywhere: several
// accounts share one real name.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faTrash, faMagnifyingGlass, faUser, faRobot, faCheck, faGripVertical, faShieldHalved, faKey,
  faCubes, faDatabase, faBrain, faXmark, faCircleInfo, faTriangleExclamation, faLock, faUserPlus,
  faArrowsRotate, faCrown, faStethoscope, faArrowRight, faChevronRight, faSort, faSortUp, faSortDown, faDiagramProject,
  faChartPie, faBuildingColumns, faMicrochip,
} from '@fortawesome/free-solid-svg-icons';
import {
  deepClone, realName, FIELD_SECTION, SCOPE_FIELDS, relativeTime, formatDate, toTime, generatePassword,
  naturalCompare, Switch, RoleBadge, StatusPill, Avatar, PasswordInput, SourceList, ConfirmDialog, StatusIcon,
  CheckRows, Spinner, Card, Row,
} from './ui';
import { useConsole, routingTargets } from './context';
import {
  CARD_GROUPS, NO_DATA_SET, assistantLabel, assistantsFor, dataSetOf, modelLabel, scopeFieldsFor, scopeOf, scopeWhat,
  usedBy, variantLabel,
} from './access';
import DataMap from './DataMap';

const ROLE_ICONS = { superadmin: faCrown, admin: faShieldHalved, user: faUser };
const ROLE_RANK = { superadmin: 0, admin: 1, user: 2 };
const isAdminRole = (role) => role === 'admin' || role === 'superadmin';

// ─────────────────────────────────────────────────────────
// Cards of the user page
// ─────────────────────────────────────────────────────────

function ProfileCard({ form, set, meta, adminMode, errors }) {
  return (
    <Card id="profile" title="Profile" description="Login name, real name and the language of the dashboard.">
      <Row label="Username" error={errors.username}
        hint={adminMode ? 'Used to log in. Letters, digits and . _ @ - only.' : 'Your login name — only an administrator can change it.'}>
        <input className="um-input-strong" value={form.username} disabled={!adminMode} autoComplete="off"
          onChange={(e) => set('username', e.target.value)} placeholder="e.g. jdoe" />
      </Row>
      <Row label="Name" error={errors.first_name || errors.last_name}>
        <div className="um-pair">
          <input aria-label="First name" value={form.first_name} placeholder="First name"
            onChange={(e) => set('first_name', e.target.value)} />
          <input aria-label="Last name" value={form.last_name} placeholder="Last name"
            onChange={(e) => set('last_name', e.target.value)} />
        </div>
      </Row>
      <Row label="E-mail" error={errors.email}>
        <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="name@company.com" />
      </Row>
      <Row label="Language" hint="Dashboard and assistants answer in it." error={errors.language}>
        <select className="um-select-short" value={form.language || 'de'} onChange={(e) => set('language', e.target.value)}>
          {meta.languages.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </Row>
    </Card>
  );
}

function PasswordRow({ isNew, isSelf, adminMode, minLen, errors, passwords, setPasswords, open, setOpen }) {
  const generate = adminMode && !isSelf
    ? () => { const pw = generatePassword(); setPasswords((p) => ({ ...p, next: pw, confirm: pw })); }
    : null;
  const keep = () => { setPasswords({ current: '', next: '', confirm: '' }); setOpen(false); };
  const mismatch = passwords.confirm && passwords.next !== passwords.confirm;
  return (
    <Row label="Password" error={errors.current_password || errors.password}
      hint={`At least ${minLen} characters. Stored as a secure hash — nobody can read it, administrators included.`}>
      {open ? (
        <div className="um-pw">
          {isSelf && !isNew && (
            <PasswordInput value={passwords.current} placeholder="Current password" autoComplete="current-password"
              onChange={(v) => setPasswords((p) => ({ ...p, current: v }))} />
          )}
          <PasswordInput value={passwords.next} placeholder={isNew ? 'Password' : 'New password'} autoComplete="new-password"
            onChange={(v) => setPasswords((p) => ({ ...p, next: v }))} onGenerate={generate} />
          <PasswordInput value={passwords.confirm} placeholder="Repeat password" autoComplete="new-password"
            onChange={(v) => setPasswords((p) => ({ ...p, confirm: v }))} />
          {mismatch && <div className="um-hint error">The passwords do not match.</div>}
          {!isNew && <button type="button" className="um-link-btn" onClick={keep}>Keep the current password</button>}
        </div>
      ) : (
        <div className="um-pw-closed">
          <span className="um-pw-dots" aria-label="Password set">••••••••••</span>
          <button type="button" className="um-btn um-btn-ghost um-btn-sm" onClick={() => setOpen(true)}>
            <FontAwesomeIcon icon={faKey} /> Change password
          </button>
        </div>
      )}
    </Row>
  );
}

function AccessCard({ form, set, meta, me, adminMode, isSelf, isNew, errors, passwords, setPasswords, pwOpen, setPwOpen }) {
  const bootstrap = meta.permissions.bootstrap_superadmin;
  const targets = routingTargets(meta);
  const home = form.ui_config.home_instance || '';
  const homeTarget = targets.find((t) => t.key === home);
  const active = (form.status || 'active') === 'active';
  // The super admin role is only offered to who may grant it — or to show it.
  const roles = meta.roles.filter((r) => r.value !== 'superadmin' || me.is_superadmin || bootstrap
    || form.role === 'superadmin');

  return (
    <Card id="access" title={adminMode ? 'Access' : 'Password'}
      description={adminMode ? 'What the user may manage, whether they can log in, and where.' : 'The password you log in with.'}>
      {adminMode && (
        <Row label="Role" hint="Administrators manage the users of this instance." error={errors.role}>
          {bootstrap && (
            <div className="um-note warn"><FontAwesomeIcon icon={faCrown} /> This instance has no super admin yet. You can make one user super admin once — afterwards only super admins grant that role.</div>
          )}
          <div className="um-radios" role="radiogroup" aria-label="Role">
            {roles.map((r) => {
              const needsSuper = (r.value === 'superadmin' || form.role === 'superadmin') && !me.is_superadmin && !bootstrap;
              const locked = (isSelf && r.value !== form.role && !(bootstrap && r.value === 'superadmin')) || needsSuper;
              const on = form.role === r.value;
              return (
                <button key={r.value} type="button" role="radio" aria-checked={on} disabled={locked}
                  className={`um-radio${on ? ' on' : ''}`} onClick={() => set('role', r.value)}
                  title={locked ? (isSelf ? 'You cannot change your own role' : 'Only a super admin can change this') : ''}>
                  <span className="um-radio-dot" />
                  <span className="um-radio-text">
                    <span className="um-radio-label"><FontAwesomeIcon icon={ROLE_ICONS[r.value] || faUser} /> {r.label}</span>
                    <span className="um-radio-desc">{r.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </Row>
      )}

      {adminMode && (
        <Row label="Status" error={errors.status}
          hint={isSelf ? 'You cannot deactivate your own account.' : 'Inactive users cannot log in; open sessions end.'}>
          <Switch checked={active} disabled={isSelf} onChange={(on) => set('status', on ? 'active' : 'inactive')}
            label={active ? 'Active — can log in' : 'Inactive — login blocked'} />
        </Row>
      )}

      {adminMode && targets.length > 0 && (
        <Row label="Instance" hint="Where the user logs in and works." error={errors['ui_config.home_instance']}>
          <select value={home} disabled={isSelf} onChange={(e) => set('ui_config.home_instance', e.target.value)}>
            <option value="">This instance{meta.instance?.instance_label ? ` — ${meta.instance.instance_label}` : ''}</option>
            {targets.map((t) => (
              <option key={t.key} value={t.key}>{t.label}{t.environment ? ` (${t.environment})` : ''}</option>
            ))}
            {home && !homeTarget && <option value={home}>{home} (no longer registered)</option>}
          </select>
          <div className="um-hint">
            {isSelf ? 'You cannot move your own login.' : home
              ? `Logging in here hands the user to ${homeTarget ? homeTarget.url : home}. Password, modules and data access are those of their account there.`
              : 'The user logs in and works on this instance.'}
          </div>
        </Row>
      )}

      <PasswordRow isNew={isNew} isSelf={isSelf} adminMode={adminMode} minLen={meta.password_min_length}
        errors={errors} passwords={passwords} setPasswords={setPasswords} open={pwOpen} setOpen={setPwOpen} />
    </Card>
  );
}

function DashboardOrder({ enabled, byKey, set, toggle }) {
  const [drag, setDrag] = useState(null);
  const move = (from, to) => {
    if (from === to) return;
    const list = [...enabled];
    const [m] = list.splice(from, 1);
    list.splice(to, 0, m);
    set('ui_config.expandables', list);
  };
  return (
    <div className="um-order">
      {enabled.map((key, idx) => (
        <div key={key}
          className={`um-order-chip${drag?.over === idx ? ' drag-over' : ''}${drag?.from === idx ? ' dragging' : ''}`}
          draggable
          onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDrag({ from: idx, over: null }); }}
          onDragOver={(e) => { e.preventDefault(); setDrag((d) => (d ? { ...d, over: idx } : d)); }}
          onDrop={(e) => { e.preventDefault(); if (drag) move(drag.from, idx); setDrag(null); }}
          onDragEnd={() => setDrag(null)}
          title="Drag to change the order">
          <FontAwesomeIcon icon={faGripVertical} className="um-grip" />
          <span className="um-order-idx">{idx + 1}</span>
          <span className="um-order-label">{byKey[key]?.label || key}</span>
          <button type="button" className="um-order-x" title="Turn off" onClick={() => toggle(key)}><FontAwesomeIcon icon={faXmark} /></button>
        </div>
      ))}
    </div>
  );
}

function ModulesCard({ form, set, meta, adminMode, errors }) {
  const enabled = form.ui_config.expandables || [];
  const byKey = useMemo(() => Object.fromEntries(meta.modules.map((m) => [m.key, m])), [meta.modules]);
  const toggle = (key) => set('ui_config.expandables', enabled.includes(key) ? enabled.filter((k) => k !== key) : [...enabled, key]);
  const setGroup = (keys, on) => set('ui_config.expandables',
    on ? [...enabled, ...keys.filter((k) => !enabled.includes(k))] : enabled.filter((k) => !keys.includes(k)));

  if (!adminMode) {
    return (
      <Card id="modules" title="Modules" description="What appears on your dashboard. An administrator assigns them.">
        <div className="um-card-body">
          {enabled.length === 0
            ? <div className="um-empty-inline">No modules assigned.</div>
            : <div className="um-chip-list">{enabled.map((k) => <span key={k} className="um-chip">{byKey[k]?.label || k}</span>)}</div>}
        </div>
      </Card>
    );
  }

  return (
    <Card id="modules" title="Modules" description="What appears on this user's dashboard."
      actions={<span className="um-card-meta">{enabled.length} of {meta.modules.length} on</span>}>
      <div className="um-group">
        <div className="um-group-head">
          <div>
            <h3>On the dashboard</h3>
            <p>{enabled.length > 1 ? 'In this order — drag to change it.' : 'Turn modules on below.'}</p>
          </div>
        </div>
        {enabled.length === 0
          ? <div className="um-empty-inline">No modules on — the dashboard will be empty.</div>
          : <DashboardOrder enabled={enabled} byKey={byKey} set={set} toggle={toggle} />}
        {errors['ui_config.expandables'] && <div className="um-hint error">{errors['ui_config.expandables']}</div>}
      </div>

      {meta.module_groups.map((g) => {
        const mods = meta.modules.filter((m) => m.group === g.key);
        const keys = mods.map((m) => m.key);
        const allOn = keys.every((k) => enabled.includes(k));
        const withText = mods.some((m) => m.description);
        return (
          <div key={g.key} className="um-group">
            <div className="um-group-head">
              <div>
                <h3>
                  {g.label}
                  {CARD_GROUPS.includes(g.key) && <span className="um-tag" title="Reads client data — see Data access"><FontAwesomeIcon icon={faDatabase} /> Client data</span>}
                </h3>
                {g.description && <p>{g.description}</p>}
              </div>
              <button type="button" className="um-link-btn" onClick={() => setGroup(keys, !allOn)}>
                {allOn ? 'Turn all off' : 'Turn all on'}
              </button>
            </div>
            <div className={`um-mod-list${withText ? '' : ' compact'}`}>
              {mods.map((m) => {
                const on = enabled.includes(m.key);
                return (
                  <label key={m.key} className={`um-mod${on ? ' on' : ''}`}>
                    <span className="um-mod-text">
                      <span className="um-mod-name">
                        {m.label}
                        {m.assistant && m.group === 'assistants' && <span className="um-tag" title="Reads client data — see Data access"><FontAwesomeIcon icon={faDatabase} /> Client data</span>}
                      </span>
                      {m.description && <span className="um-mod-desc">{m.description}</span>}
                    </span>
                    <span className="um-switch">
                      <input type="checkbox" checked={on} onChange={() => toggle(m.key)} />
                      <span className="um-switch-track" />
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="um-group">
        <div className="um-group-head"><div><h3>Dashboard extras</h3></div></div>
        <Row label="Breaches button" hint="Portfolio-breaches shortcut in the top bar.">
          <Switch checked={form.ui_config.show_breaches_button} onChange={(v) => set('ui_config.show_breaches_button', v)}
            label={form.ui_config.show_breaches_button ? 'Shown' : 'Hidden'} />
        </Row>
        <Row label="Hero image" hint="Optional banner above the dashboard." error={errors['ui_config.hero_image']}>
          <input value={form.ui_config.hero_image || ''} onChange={(e) => set('ui_config.hero_image', e.target.value)}
            placeholder="/hero/welcome.png or https://…" />
        </Row>
      </div>
    </Card>
  );
}

function ScopeRow({ field, ui, set, values, known, error }) {
  const def = SCOPE_FIELDS[field];
  const value = ui[field] || '';
  const unknown = value && known && known.length > 0 && !known.includes(value);
  const listId = `um-dl-${field}`;
  return (
    <Row label={def.label} hint={def.hint} error={error}>
      <input list={listId} value={value} placeholder={def.placeholder} className={error ? 'error' : ''}
        onChange={(e) => set(`ui_config.${field}`, e.target.value)} />
      {values && values.length > 0 && (
        <datalist id={listId}>{values.map((v) => <option key={v} value={v} />)}</datalist>
      )}
      {unknown && <div className="um-hint warn"><FontAwesomeIcon icon={faTriangleExclamation} /> Not found in the data — the user would see nothing.</div>}
      {field === 'ppi_consolidation' && value && ui.ppi_portfolio && <div className="um-hint">Ignored while a portfolio is set — the portfolio wins.</div>}
    </Row>
  );
}

function DataCard({ form, set, meta, errors, options, loadingOptions, reloadOptions, original, isNew, dirty }) {
  const { client } = useConsole();
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const ui = form.ui_config;
  const assistants = assistantsFor(ui, meta);
  const chosen = ui.data_sources || {};
  const sets = meta.data_sources || [];
  const defaults = meta.assistant_data_sets || {};
  const results = Object.fromEntries((check?.results || []).map((r) => [r.assistant || r.profile, r]));
  const canCheck = !isNew && !dirty && assistants.length > 0;

  const runCheck = async () => {
    setChecking(true);
    setCheck(null);
    try { setCheck(await client.post(`/api/users/${original.id}/check`)); } catch (e) { setCheck({ error: e.message }); }
    setChecking(false);
  };
  const valuesState = (src) => {
    if (loadingOptions) return <span className="um-values-state"><Spinner small /> Loading values…</span>;
    if (!src) return null;
    return src.ok
      ? <span className="um-values-state ok"><FontAwesomeIcon icon={faCheck} /> Suggestions from the data</span>
      : <span className="um-values-state" title={src.error}>No suggestions — type the value</span>;
  };

  return (
    <Card id="data" title="Data access"
      description="Which client data this user's assistants (and the EFD audio cards) read. The filter is enforced on the server for every query."
      actions={assistants.length > 0 && (
        <button type="button" className="um-btn um-btn-ghost um-btn-sm" onClick={runCheck} disabled={!canCheck || checking}
          title={canCheck ? 'Runs real queries with this user\'s settings' : 'Save first — the check uses the stored settings'}>
          {checking ? <Spinner small /> : <FontAwesomeIcon icon={faStethoscope} />} Check access
        </button>
      )}>
      {assistants.length === 0 && (
        <div className="um-card-body">
          <div className="um-empty-inline">None of this user&apos;s modules reads client data. Turn on Assistant Bro EFD, Assistant Bro PPI or EFD audio cards under Modules.</div>
        </div>
      )}

      {assistants.map((a) => {
        const model = meta.assistants?.find((x) => x.key === a)?.model || a;
        const found = dataSetOf(a, ui, sets, defaults);
        const fields = scopeFieldsFor(a, found.variant, ui, meta.modules);
        const scope = scopeOf(a, found.variant, ui, fields);
        const opts = a === 'ppi' ? options?.ppi : options?.mh;
        const cons = ui.ppi_consolidation;
        const result = results[a];
        const fitting = sets.filter((x) => x.model === model);
        const def = sets.find((x) => x.key === defaults[a]);
        const value = chosen[a] || '';
        return (
          <div key={a} className="um-group">
            <div className="um-group-head">
              <div>
                <h3><FontAwesomeIcon icon={a === 'ppi' ? faChartPie : faBuildingColumns} className="um-group-icon" /> {assistantLabel(meta, a)}</h3>
                <p>Used by {usedBy(a, ui, meta.modules).join(', ')}</p>
              </div>
              <span className={`um-scope-pill${!found.variant ? ' none' : scope.limited ? ' limited' : ''}`} title="What this user sees">
                {!found.variant ? 'No access' : scope.limited ? `Only ${scopeWhat(scope)}` : scope.value}
              </span>
            </div>
            <Row label="Whose data" error={errors['ui_config.data_sources']}
              hint={`The ${model.toUpperCase()} data set this user reads. Only ${model.toUpperCase()} data sets fit.`}>
              <select value={value} onChange={(e) => set('ui_config.data_sources', { ...chosen, [a]: e.target.value })}>
                <option value="">Instance default — {def ? def.name : 'none set'}</option>
                {fitting.map((x) => (
                  <option key={x.key} value={x.key}>{x.name}{!x.variant ? ' (variant not set)' : variantLabel(meta, x.variant) !== 'Standard' ? ` — variant ${variantLabel(meta, x.variant)}` : ''}</option>
                ))}
                <option value={NO_DATA_SET}>No access</option>
                {value && value !== NO_DATA_SET && !fitting.some((x) => x.key === value) && <option value={value}>{value} (missing)</option>}
              </select>
              {found.set && (
                <div className="um-hint">
                  Variant {variantLabel(meta, found.set.variant) || 'not set'}
                  {found.set.schemas?.length ? ` · reads only ${found.set.schemas.join(', ')}` : ''}
                </div>
              )}
              {!found.variant && <div className="um-hint warn"><FontAwesomeIcon icon={faTriangleExclamation} /> This user sees no {model.toUpperCase()} data — the tile is hidden.</div>}
            </Row>
            {fields.includes('rm_name') && (
              <ScopeRow field="rm_name" ui={ui} set={set} values={opts?.rm_names} known={opts?.rm_names}
                error={errors['ui_config.rm_name']} />
            )}
            {fields.includes('ppi_consolidation') && (
              <ScopeRow field="ppi_consolidation" ui={ui} set={set} values={opts?.consolidations} known={opts?.consolidations}
                error={errors['ui_config.ppi_consolidation']} />
            )}
            {fields.includes('ppi_portfolio') && (
              <ScopeRow field="ppi_portfolio" ui={ui} set={set}
                values={(opts?.portfolios || []).filter((x) => !cons || x.consolidation === cons).map((x) => x.value)}
                known={(opts?.portfolios || []).map((x) => x.value)}
                error={errors['ui_config.ppi_portfolio']} />
            )}
            {found.variant === 'efd_survista' && (
              <Row label="Scope"><span className="um-static">Whole book — this variant has no advisor filter.</span></Row>
            )}
            {fields.length > 0 && (
              <div className="um-group-foot">
                {valuesState(opts)}
                <button type="button" className="um-link-btn" onClick={reloadOptions} disabled={loadingOptions}>
                  <FontAwesomeIcon icon={faArrowsRotate} /> Reload values
                </button>
              </div>
            )}
            {result && (
              <div className={`um-check-card ${result.status}`}>
                <div className="um-check-card-head">
                  <StatusIcon status={result.status} />
                  <strong>Check</strong>
                  <span className="um-muted">via {result.source?.name} · scope: {result.scope}{result.whole_data ? ' (all data)' : ''}</span>
                </div>
                {result.error && <div className="um-hint error">{result.error}</div>}
                <CheckRows rows={result.relations || []} />
              </div>
            )}
          </div>
        );
      })}

      {check?.error && <div className="um-card-body"><div className="um-hint error">{check.error}</div></div>}
      {(isNew || dirty) && assistants.length > 0 && (
        <div className="um-card-foot"><FontAwesomeIcon icon={faCircleInfo} /> Save first to check access — the check uses the stored settings.</div>
      )}
    </Card>
  );
}

// The model Assistant Bro EFD / PPI answer with for this user — answers,
// welcome snapshot and language detection (src/data_agent/llm_choice.py).
function LlmCard({ form, set, meta, errors }) {
  const current = form.llm_config.agent_model || '';
  const models = meta.agent_models || [];
  const chosen = models.find((m) => m.value === current);
  return (
    <Card id="llm" title="Language model"
      description="The model Assistant Bro EFD and PPI answer with for this user. The questions and the figures Bro reads are sent to its provider.">
      <Row label="Model" hint="System default = the instance's model (GPT-5.2)." error={errors['llm_config.agent_model']}>
        <select value={current} onChange={(e) => set('llm_config.agent_model', e.target.value)}>
          <option value="">System default</option>
          {models.map((m) => (
            <option key={m.value} value={m.value} disabled={!m.available && m.value !== current}>
              {m.label} · {m.provider}{m.available ? '' : ' (no API key)'}
            </option>
          ))}
          {current && !chosen && <option value={current}>{current} (legacy)</option>}
        </select>
      </Row>
      {chosen && !chosen.available && (
        <div className="um-card-body">
          <div className="um-hint error">No {chosen.provider} API key on this instance — Bro uses the system default.</div>
        </div>
      )}
    </Card>
  );
}

function AiCard({ form, set, meta, botFunctions, errors }) {
  const ids = form.llm_config.bot_function_ids || [];
  const [onlySome, setOnlySome] = useState(ids.length > 0);
  const [q, setQ] = useState('');
  const current = form.llm_config.llm_model || '';
  const temperature = form.llm_config.temperature ?? 0.7;
  const filtered = botFunctions.filter((f) => !q || `${f.name} ${f.description}`.toLowerCase().includes(q.toLowerCase()));
  const toggleFn = (id) => set('llm_config.bot_function_ids', ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

  return (
    <Card id="ai" title="Assistant Bro" description="Model, instructions and tools of Assistant Bro and the chat widget for this user.">
      <Row label="Model" hint="A model without an API key falls back to the default." error={errors['llm_config.llm_model']}>
        <select value={current} onChange={(e) => set('llm_config.llm_model', e.target.value)}>
          <option value="">System default</option>
          {meta.models.map((m) => (
            <option key={m.value} value={m.value} disabled={!m.available && m.value !== current}>
              {m.label} · {m.provider}{m.available ? '' : ' (no API key)'}
            </option>
          ))}
          {current && !meta.models.some((m) => m.value === current) && <option value={current}>{current} (legacy)</option>}
        </select>
      </Row>
      <Row label="Temperature" hint="Lower = more precise, higher = more creative. GPT-5 models ignore it." error={errors['llm_config.temperature']}>
        <div className="um-range">
          <span className="um-range-end">Precise</span>
          <input type="range" min="0" max="1" step="0.05" value={temperature}
            onChange={(e) => set('llm_config.temperature', parseFloat(e.target.value))} />
          <span className="um-range-end">Creative</span>
          <output className="um-range-value">{temperature.toFixed(2)}</output>
        </div>
      </Row>
      <Row label="System prompt" hint="Empty = the standard Assistant Bro prompt." stack error={errors['llm_config.system_prompt']}>
        <textarea rows={5} value={form.llm_config.system_prompt || ''} onChange={(e) => set('llm_config.system_prompt', e.target.value)}
          placeholder="Custom instructions for this user's assistant…" />
      </Row>
      <Row label="Functions" hint="The tools Assistant Bro may call." stack error={errors['llm_config.bot_function_ids']}>
        <div className="um-segmented">
          <button type="button" className={onlySome ? '' : 'on'} onClick={() => { setOnlySome(false); set('llm_config.bot_function_ids', []); }}>
            All functions
          </button>
          <button type="button" className={onlySome ? 'on' : ''} onClick={() => setOnlySome(true)}>Only selected</button>
        </div>
        {onlySome && (
          <div className="um-fn-box">
            <div className="um-fn-tools">
              <div className="um-search small">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter functions…" />
              </div>
              <span className="um-card-meta">{ids.length} of {botFunctions.length} selected</span>
              <button type="button" className="um-link-btn" onClick={() => set('llm_config.bot_function_ids', botFunctions.map((f) => f.id))}>Select all</button>
              <button type="button" className="um-link-btn" onClick={() => set('llm_config.bot_function_ids', [])}>Clear</button>
            </div>
            <div className="um-fn-list">
              {filtered.length === 0 && <div className="um-empty-inline">No function matches.</div>}
              {filtered.map((f) => {
                const on = ids.includes(f.id);
                return (
                  <label key={f.id} className={`um-fn${on ? ' on' : ''}`}>
                    <input type="checkbox" className="um-cb" checked={on} onChange={() => toggleFn(f.id)} />
                    <span className="um-fn-text">
                      <span className="um-fn-name">{f.name}</span>
                      {f.description && <span className="um-fn-desc">{f.description}</span>}
                    </span>
                  </label>
                );
              })}
            </div>
            {ids.length === 0 && (
              <div className="um-hint warn"><FontAwesomeIcon icon={faTriangleExclamation} /> Nothing selected yet — until you pick one, all functions stay available.</div>
            )}
          </div>
        )}
      </Row>
    </Card>
  );
}

function AreCard({ form, set, adminMode }) {
  return (
    <Card id="are" title="Reasoning Engine" description={`Sources the Advisor Reasoning Engine uses for ${adminMode ? 'this user' : 'you'}.`}>
      {adminMode && (
        <Row label="theScreener data" hint="Security research data from theScreener.">
          <Switch checked={form.ui_config.are_screener_enabled !== false} onChange={(v) => set('ui_config.are_screener_enabled', v)}
            label={form.ui_config.are_screener_enabled !== false ? 'Used' : 'Never used'} />
        </Row>
      )}
      <Row label="Own sources" hint="Links or search pages used in addition. The priority weights how much they are trusted." stack>
        <SourceList items={form.ui_config.are_sources || []} onChange={(v) => set('ui_config.are_sources', v)} withPriority
          labelPlaceholder="Label (e.g. Reuters)" urlPlaceholder="https://…" addLabel="Add source" />
      </Row>
      <Row label="Excluded sources" hint="Never used — matched by domain or name." stack>
        <SourceList items={form.ui_config.are_excluded_sources || []} onChange={(v) => set('ui_config.are_excluded_sources', v)} danger
          labelPlaceholder="Name (e.g. Reddit)" urlPlaceholder="Domain or URL (e.g. reddit.com)" addLabel="Add exclusion" />
      </Row>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────
// Summary column: who this is, plus the section links
// ─────────────────────────────────────────────────────────

function Summary({ form, original, isNew, isSelf, meta, adminMode, sections, active, onJump, errorSections }) {
  const targets = routingTargets(meta);
  const home = form.ui_config.home_instance || '';
  const target = targets.find((t) => t.key === home);
  const name = realName(form);
  return (
    <aside className="um-side">
      <div className="um-id">
        <Avatar username={form.username} size="xl" />
        <div className="um-id-user">{form.username || <span className="um-muted">{isNew ? 'New user' : '—'}</span>}</div>
        <div className="um-id-name">{name || <span className="um-muted">No name set</span>}</div>
        {form.email && <div className="um-id-mail" title={form.email}>{form.email}</div>}
        <div className="um-id-badges">
          <RoleBadge role={form.role} />
          <StatusPill status={form.status} />
          {isSelf && <span className="um-badge um-badge-you">You</span>}
        </div>
        <dl className="um-id-facts">
          {adminMode && targets.length > 0 && (
            <div><dt>Instance</dt><dd title={target?.url || ''}>{home ? (target?.label || home) : 'This instance'}</dd></div>
          )}
          <div><dt>Modules</dt><dd>{(form.ui_config.expandables || []).length}</dd></div>
          {!isNew && <div><dt>Last login</dt><dd title={formatDate(original.last_login)}>{relativeTime(original.last_login)}</dd></div>}
          {!isNew && original.created_at && <div><dt>Created</dt><dd>{formatDate(original.created_at, false)}</dd></div>}
        </dl>
      </div>
      <nav className="um-side-nav" aria-label="Sections">
        {sections.map((s) => (
          <button key={s.key} type="button" onClick={() => onJump(s.key)}
            className={`${active === s.key ? 'active' : ''}${errorSections.has(s.key) ? ' has-error' : ''}`}>
            <FontAwesomeIcon icon={s.icon} fixedWidth />
            <span>{s.label}</span>
            {errorSections.has(s.key)
              ? <FontAwesomeIcon icon={faTriangleExclamation} className="um-side-err" />
              : s.count !== undefined && <span className="um-side-count">{s.count}</span>}
          </button>
        ))}
      </nav>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────
// User page
// ─────────────────────────────────────────────────────────

export function Editor({ original, isNew, botFunctions, onClose, onSaved, onDeleted, flash, standalone }) {
  const { client, meta, me } = useConsole();
  const adminMode = meta.permissions.manage_users;
  const isSelf = !isNew && original.id === me.id;
  // Only a super admin edits a super admin (the server enforces it too).
  const lockedSuper = adminMode && !isNew && original.role === 'superadmin' && !me.is_superadmin;
  const [form, setForm] = useState(() => deepClone(original));
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [pwOpen, setPwOpen] = useState(isNew);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);       // 'discard' | 'delete'
  const [options, setOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [active, setActive] = useState('profile');
  const spyLock = useRef(0);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(original) || !!passwords.next || !!passwords.current,
    [form, original, passwords],
  );

  const enabled = form.ui_config.expandables || [];
  const assistants = assistantsFor(form.ui_config, meta);
  const sections = [
    { key: 'profile', label: 'Profile', icon: faUser },
    { key: 'access', label: adminMode ? 'Access' : 'Password', icon: adminMode ? faKey : faLock },
    { key: 'modules', label: 'Modules', icon: faCubes, count: enabled.length },
    ...(adminMode ? [{ key: 'data', label: 'Data access', icon: faDatabase }] : []),
    ...(adminMode && assistants.length ? [{ key: 'llm', label: 'Language model', icon: faMicrochip }] : []),
    ...(adminMode && (enabled.includes('chat') || enabled.includes('chat_widget'))
      ? [{ key: 'ai', label: 'Assistant Bro', icon: faRobot }] : []),
    ...(enabled.includes('advisor_reasoning') ? [{ key: 'are', label: 'Reasoning Engine', icon: faBrain }] : []),
  ];
  const sectionKeys = sections.map((s) => s.key).join(',');
  const errorSections = new Set(Object.keys(errors).map((f) => FIELD_SECTION[f]).filter(Boolean));

  const loadOptions = useCallback(async (refresh = false) => {
    setLoadingOptions(true);
    try {
      const query = [isNew ? '' : `user_id=${original.id}`, refresh ? 'refresh=1' : ''].filter(Boolean).join('&');
      setOptions(await client.get(`/api/users/scope-options${query ? `?${query}` : ''}`));
    } catch (e) {
      setOptions({ mh: { ok: false, error: e.message }, ppi: { ok: false, error: e.message } });
    } finally {
      setLoadingOptions(false);
    }
  }, [client, isNew, original.id]);

  // Suggestions for the scope fields — only when the user has one.
  const needsOptions = adminMode && assistants.length > 0;
  useEffect(() => {
    if (needsOptions && !options && !loadingOptions) loadOptions();
  }, [needsOptions, options, loadingOptions, loadOptions]);

  // Warn before closing the browser tab with unsaved changes.
  useEffect(() => {
    if (!dirty) return undefined;
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  // Highlight the section in view (the side nav follows the scroll).
  useEffect(() => {
    const keys = sectionKeys.split(',');
    const onScroll = () => {
      if (Date.now() < spyLock.current) return;
      let current = keys[0];
      keys.forEach((k) => {
        const el = document.getElementById(`um-sec-${k}`);
        if (el && el.getBoundingClientRect().top <= 150) current = k;
      });
      const doc = document.documentElement;
      if (window.innerHeight + window.scrollY >= doc.scrollHeight - 4 && window.scrollY > 0) current = keys[keys.length - 1];
      setActive(current);
    };
    document.addEventListener('scroll', onScroll, true);
    onScroll();
    return () => document.removeEventListener('scroll', onScroll, true);
  }, [sectionKeys]);

  const jump = useCallback((key) => {
    const el = document.getElementById(`um-sec-${key}`);
    if (!el) return;
    spyLock.current = Date.now() + 700;
    setActive(key);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const set = (path, val) => {
    setForm((prev) => {
      const next = deepClone(prev);
      const keys = path.split('.');
      let o = next;
      for (let i = 0; i < keys.length - 1; i += 1) o = o[keys[i]];
      o[keys[keys.length - 1]] = val;
      return next;
    });
    setErrors((prev) => {
      if (!prev[path]) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });
  };

  const requestClose = () => (dirty ? setConfirm('discard') : onClose());

  const discard = () => {
    setForm(deepClone(original));
    setPasswords({ current: '', next: '', confirm: '' });
    setPwOpen(false);
    setErrors({});
  };

  const buildPayload = () => {
    let payload;
    if (adminMode) {
      payload = {
        first_name: form.first_name, last_name: form.last_name, email: form.email, language: form.language,
        username: form.username, status: form.status || 'active', role: form.role,
        ui_config: form.ui_config, llm_config: form.llm_config,
      };
    } else {
      payload = {
        first_name: form.first_name, last_name: form.last_name, email: form.email, language: form.language,
        ui_config: { are_sources: form.ui_config.are_sources || [], are_excluded_sources: form.ui_config.are_excluded_sources || [] },
      };
    }
    if (passwords.next) {
      payload.password = passwords.next;
      if (isSelf) payload.current_password = passwords.current;
    }
    return payload;
  };

  const validate = () => {
    const errs = {};
    if (!form.username?.trim()) errs.username = 'Username is required';
    if (isNew && !passwords.next) errs.password = 'Set an initial password';
    if (passwords.next && passwords.next.length < meta.password_min_length) errs.password = `At least ${meta.password_min_length} characters`;
    if (passwords.next && passwords.next !== passwords.confirm) errs.password = 'Passwords do not match';
    if (isSelf && passwords.next && !passwords.current) errs.current_password = 'Enter your current password';
    return errs;
  };

  const showErrors = (errs, message) => {
    setErrors(errs);
    if (errs.password || errs.current_password) setPwOpen(true);
    const first = Object.keys(errs).map((f) => FIELD_SECTION[f]).find(Boolean);
    if (first) setTimeout(() => jump(first), 0);
    if (message) flash('error', message);
  };

  const save = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { showErrors(errs, Object.values(errs)[0]); return; }
    setSaving(true);
    try {
      const saved = isNew
        ? await client.post('/api/users', buildPayload())
        : await client.put(`/api/users/${original.id}`, buildPayload());
      setPasswords({ current: '', next: '', confirm: '' });
      setErrors({});
      flash('success', isNew ? `User ${saved.username} created.` : 'Changes saved.');
      onSaved(saved, isNew);
    } catch (e) {
      const errs = Object.fromEntries((e.fields || []).map((f) => [f, e.message]));
      showErrors(errs, e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setConfirm(null);
    try {
      await client.del(`/api/users/${original.id}`);
      flash('success', `User ${original.username} deleted.`);
      onDeleted(original.id);
    } catch (e) {
      flash('error', e.message);
    }
  };

  const routedHome = original.ui_config?.home_instance || '';
  const routedTo = routedHome
    ? (routingTargets(meta).find((t) => t.key === routedHome) || { label: routedHome, url: routedHome })
    : null;
  const cardProps = { form, set, meta, adminMode, errors };

  return (
    <div className="um-page">
      <div className="um-crumbs">
        <button type="button" className="um-back-btn" onClick={requestClose} title="Back"><FontAwesomeIcon icon={faArrowLeft} /></button>
        <button type="button" className="um-crumb-link" onClick={requestClose}>{standalone ? 'Dashboard' : 'Users'}</button>
        <FontAwesomeIcon icon={faChevronRight} className="um-crumb-sep" />
        <span className="um-crumb-current">{isNew ? 'New user' : standalone ? 'My profile' : original.username}</span>
      </div>

      <div className="um-layout">
        <Summary form={form} original={original} isNew={isNew} isSelf={isSelf} meta={meta} adminMode={adminMode}
          sections={sections} active={active} onJump={jump} errorSections={errorSections} />

        <div className="um-main">
          {lockedSuper && (
            <div className="um-note warn"><FontAwesomeIcon icon={faCrown} /> {original.username} is a super admin — only a super admin can change this account.</div>
          )}
          {adminMode && routedTo && (
            <div className="um-note warn"><FontAwesomeIcon icon={faCircleInfo} /> {original.username} works on {routedTo.label}: they log in there, so password, modules and data access set here have no effect. Manage them on {routedTo.url}.</div>
          )}

          <ProfileCard {...cardProps} />
          <AccessCard {...cardProps} me={me} isSelf={isSelf} isNew={isNew}
            passwords={passwords} setPasswords={setPasswords} pwOpen={pwOpen} setPwOpen={setPwOpen} />
          <ModulesCard {...cardProps} />
          {adminMode && (
            <DataCard {...cardProps} options={options} loadingOptions={loadingOptions} reloadOptions={() => loadOptions(true)}
              original={original} isNew={isNew} dirty={dirty} />
          )}
          {sections.some((s) => s.key === 'llm') && <LlmCard {...cardProps} />}
          {sections.some((s) => s.key === 'ai') && <AiCard {...cardProps} botFunctions={botFunctions} />}
          {sections.some((s) => s.key === 'are') && <AreCard {...cardProps} />}

          {adminMode && !isNew && !isSelf && (
            <Card title="Delete user" tone="danger"
              description="Removes the account for good. Chats, reports and notes stay but lose their owner. Deactivating under Access is usually the better choice."
              actions={(
                <button type="button" className="um-btn um-btn-danger" onClick={() => setConfirm('delete')} disabled={lockedSuper}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
              )} />
          )}
        </div>
      </div>

      {(dirty || isNew) && (
        <div className="um-savebar" role="region" aria-label="Save changes">
          <span className="um-savebar-text">
            <i className="um-savebar-dot" />
            {isNew ? 'New user — set at least a username and a password.' : 'Unsaved changes'}
          </span>
          <div className="um-savebar-btns">
            <button type="button" className="um-btn um-btn-ghost" onClick={isNew ? requestClose : discard}>{isNew ? 'Cancel' : 'Discard'}</button>
            <button type="button" className="um-btn um-btn-primary" onClick={save} disabled={saving || lockedSuper}>
              <FontAwesomeIcon icon={isNew ? faUserPlus : faCheck} /> {saving ? 'Saving…' : isNew ? 'Create user' : 'Save changes'}
            </button>
          </div>
        </div>
      )}

      {confirm === 'discard' && (
        <ConfirmDialog title="Discard changes?" text="Your unsaved changes will be lost." confirmLabel="Discard"
          danger onConfirm={() => { setConfirm(null); onClose(); }} onCancel={() => setConfirm(null)} />
      )}
      {confirm === 'delete' && (
        <ConfirmDialog title={`Delete ${original.username}?`} text="This cannot be undone. Deactivating keeps the account and blocks the login instead."
          confirmLabel="Delete user" danger onConfirm={remove} onCancel={() => setConfirm(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// List
// ─────────────────────────────────────────────────────────

const FILTERS = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'admin', label: 'Admins', test: (u) => isAdminRole(u.role) },
  { key: 'user', label: 'Users', test: (u) => !isAdminRole(u.role) },
  { key: 'inactive', label: 'Inactive', test: (u) => u.status === 'inactive' },
];

const SORTS = {
  username: (a, b) => naturalCompare(a.username, b.username),
  role: (a, b) => ((ROLE_RANK[a.role] ?? 9) - (ROLE_RANK[b.role] ?? 9)) || naturalCompare(a.username, b.username),
  last_login: (a, b) => (toTime(b.last_login) - toTime(a.last_login)) || naturalCompare(a.username, b.username),
};

function SortHead({ id, label, sort, setSort }) {
  const on = sort.key === id;
  return (
    <button type="button" className={`um-th${on ? ' on' : ''}`}
      onClick={() => setSort(on ? { key: id, dir: -sort.dir } : { key: id, dir: 1 })}>
      {label}
      <FontAwesomeIcon icon={on ? (sort.dir === 1 ? faSortUp : faSortDown) : faSort} />
    </button>
  );
}

export function UserList({ users, onOpen, onNew, loading, reload }) {
  const { meta, me } = useConsole();
  const targets = routingTargets(meta);
  const withInstance = targets.length > 0;
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'username', dir: 1 });
  const [mapOpen, setMapOpen] = useState(false);
  const moduleLabel = useMemo(() => Object.fromEntries(meta.modules.map((m) => [m.key, m.label])), [meta.modules]);
  const counts = Object.fromEntries(FILTERS.map((f) => [f.key, users.filter(f.test).length]));
  const s = search.trim().toLowerCase();
  const shown = users
    .filter(FILTERS.find((f) => f.key === filter).test)
    .filter((u) => !s || [u.username, u.first_name, u.last_name, u.email].some((v) => v?.toLowerCase().includes(s)))
    .sort((a, b) => SORTS[sort.key](a, b) * sort.dir);

  return (
    <>
      <div className="um-list-head">
        <div>
          <h2 className="um-h2">Users <span className="um-h2-count">{users.length}</span></h2>
          <p className="um-list-sub">Everyone who can log in to this instance. Open a user to change role, modules and data access.</p>
        </div>
        <div className="um-list-actions">
          <button type="button" className="um-icon-btn" onClick={reload} title="Reload"><FontAwesomeIcon icon={faArrowsRotate} /></button>
          <button type="button" className="um-btn um-btn-ghost" onClick={() => setMapOpen(true)}
            title="A map of which person can see which data, and from which database">
            <FontAwesomeIcon icon={faDiagramProject} /> Who sees which data
          </button>
          <button type="button" className="um-btn um-btn-primary" onClick={onNew}><FontAwesomeIcon icon={faUserPlus} /> New user</button>
        </div>
      </div>

      <div className="um-toolbar">
        <div className="um-search">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <input placeholder="Search username, name or e-mail…" value={search} onChange={(e) => setSearch(e.target.value)} />
          {search && <button type="button" className="um-icon-btn" onClick={() => setSearch('')} title="Clear"><FontAwesomeIcon icon={faXmark} /></button>}
        </div>
        <div className="um-filter" role="tablist" aria-label="Filter">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" role="tab" aria-selected={filter === f.key}
              className={filter === f.key ? 'on' : ''} onClick={() => setFilter(f.key)}>
              {f.label} <span>{counts[f.key]}</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="um-loading"><Spinner /></div>
      ) : shown.length === 0 ? (
        <div className="um-empty">{s ? `No user matches “${search.trim()}”.` : 'No users in this view.'}</div>
      ) : (
        <div className={`um-ulist${withInstance ? '' : ' no-inst'}`} role="table">
          <div className="um-ulist-head" role="row">
            <SortHead id="username" label="User" sort={sort} setSort={setSort} />
            <SortHead id="role" label="Role" sort={sort} setSort={setSort} />
            {withInstance && <span className="um-th">Instance</span>}
            <span className="um-th">Modules</span>
            <span className="um-th">Data access</span>
            <SortHead id="last_login" label="Last login" sort={sort} setSort={setSort} />
            <span />
          </div>
          {shown.map((u) => {
            const ui = u.ui_config || {};
            const inactive = u.status === 'inactive';
            const routed = ui.home_instance ? (targets.find((x) => x.key === ui.home_instance) || { label: ui.home_instance }) : null;
            const mods = ui.expandables || [];
            const modLabels = mods.map((k) => moduleLabel[k] || k);
            const assistants = assistantsFor(ui, meta);
            const sub = [realName(u), u.email].filter(Boolean).join(' · ');
            return (
              <button key={u.id} type="button" role="row" className={`um-urow${inactive ? ' inactive' : ''}`} onClick={() => onOpen(u)}>
                <span className="um-ucell um-ucell-user">
                  <Avatar username={u.username} />
                  <span className="um-u-text">
                    <span className="um-u-line">
                      <span className="um-u-name">{u.username}</span>
                      {u.id === me.id && <span className="um-badge um-badge-you">You</span>}
                      {inactive && <span className="um-badge um-badge-off">Inactive</span>}
                    </span>
                    <span className="um-u-sub">{sub || 'No name or e-mail'}</span>
                  </span>
                </span>
                <span className="um-ucell"><RoleBadge role={u.role} /></span>
                {withInstance && (
                  <span className="um-ucell">
                    {routed
                      ? <span className="um-route" title={routed.url || ''}><FontAwesomeIcon icon={faArrowRight} /> {routed.label}</span>
                      : <span className="um-muted">This instance</span>}
                  </span>
                )}
                {routed ? (
                  <span className="um-ucell um-ucell-wide um-muted">Managed on {routed.label}</span>
                ) : (
                  <>
                    <span className="um-ucell um-ucell-text" title={modLabels.join(', ')}>
                      {mods.length
                        ? <><span className="um-num">{mods.length}</span><span className="um-ellipsis">{modLabels.join(', ')}</span></>
                        : <span className="um-muted">None</span>}
                    </span>
                    <span className="um-ucell um-ucell-text">
                      {assistants.length === 0 ? <span className="um-muted">No client data</span> : (
                        <span className="um-ellipsis" title="Whose data each assistant reads">
                          {assistants.map((a) => {
                            const found = dataSetOf(a, ui, meta.data_sources || [], meta.assistant_data_sets);
                            const sc = scopeOf(a, found.variant, ui, scopeFieldsFor(a, found.variant, ui, meta.modules));
                            return (
                              <span key={a} className={`um-scope-item${!found.variant ? ' none' : sc.limited ? ' limited' : ''}`}>
                                <b>{modelLabel(a)}</b> {!found.variant ? 'no access'
                                  : `${found.set ? found.set.name : 'server default'}${sc.limited ? ` · ${sc.value}` : ''}`}
                              </span>
                            );
                          })}
                        </span>
                      )}
                    </span>
                  </>
                )}
                <span className="um-ucell um-ucell-login" title={formatDate(u.last_login)}>{relativeTime(u.last_login)}</span>
                <FontAwesomeIcon icon={faChevronRight} className="um-urow-go" />
              </button>
            );
          })}
        </div>
      )}

      {mapOpen && (
        <DataMap users={users} onClose={() => setMapOpen(false)}
          onOpenUser={(u) => { setMapOpen(false); onOpen(u); }} />
      )}
    </>
  );
}
